/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { NewsScoreStateService } from '../news-score-state.service';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import {
  BENCH_LEVELS,
  INDUSTRY_BENCH_SCORES,
} from 'src/app/shared/utils/score-type-details';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import {
  ScoreTimeSeriesResponse,
  ScoreRequestTypes,
} from 'src/app/shared/models/score-time-series.interface';

interface ScoreResponse {
  score: number | null;
  trend_value?: string;
  trend_type?: 'bear' | 'bull';
  sentiment_delta?: number;
  score_ts?: { score: number }[];
}

export interface AuditData {
  entity: {
    alpha_id?: string;
    name: string;
    type: string;
    description?: any;
    sanctions?: any[];
  };
  sentiment?: {
    sentiment?: number;
    sentiment_delta?: number;
  };
  score?: number;
  trend_value?: string;
  trend_type?: 'bear' | 'bull';
  industryBenchmarkScore?: number;
  industryBenchmarkDescription?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuditDataService {
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  constructor(
    private newsScoreState: NewsScoreStateService,
    private featureFlagsService: FeatureFlagsService,
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private sentimentService: SentimentService,
    private portfolioApi: PortfolioApiService,
  ) {}

  public getAuditData(params: any, baseData: AuditData): Observable<AuditData> {
    this.loadingSubject.next(true);

    const defaultScore = this.featureFlagsService.scorelabSettings.defaultScore;
    const isSentiment = defaultScore === 'sentiment';
    const isBullBear = defaultScore === 'bb';

    const period = params.period || '7';

    const scoreType: ScoreRequestTypes = isSentiment
      ? { period }
      : this.scoreUtils.resolveScoreType(defaultScore);

    const score$ = this.fetchScore(scoreType, params);
    const entityInfo$ = this.getEntityInformation(params);
    const industryBenchmark$ = this.getIndustryBenchmark(defaultScore, params);

    return forkJoin({
      scoreResult: score$,
      benchmarkResult: industryBenchmark$,
      entityInfo: entityInfo$,
    }).pipe(
      map(({ scoreResult, benchmarkResult, entityInfo }) => {
        const auditData = { ...baseData, ...entityInfo };

        if (scoreResult) {
          if (isSentiment) {
            delete auditData.score;
            auditData.sentiment = {
              sentiment: scoreResult.score,
              sentiment_delta: scoreResult.sentiment_delta,
            };
          } else if (isBullBear) {
            auditData.score = scoreResult.score;
            auditData.trend_value = scoreResult.trend_value;
            auditData.trend_type = scoreResult.trend_type;
          } else {
            auditData.score =
              scoreResult.score !== null
                ? Number(scoreResult.score.toFixed(1))
                : null;
          }
        }

        this.processBenchmark(auditData, benchmarkResult);

        return auditData;
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  private getIndustryBenchmark(
    scoreType: string,
    params: any,
  ): Observable<ScoreResponse | null> {
    if (
      !INDUSTRY_BENCH_SCORES.includes(scoreType) ||
      params.type !== 'company'
    ) {
      return of(null);
    }

    return this.newsScoreState.fetchIndustryBenchmark(scoreType, params).pipe(
      map((res: any) => ({ score: res.score_ts_last_score?.score ?? null })),
      catchError(() => of(null)),
    );
  }

  private processBenchmark(
    auditData: AuditData,
    benchmark: ScoreResponse | null,
  ): void {
    if (!benchmark || benchmark.score === null) {
      auditData.industryBenchmarkScore = null;
      auditData.industryBenchmarkDescription = '';
      return;
    }

    auditData.industryBenchmarkScore = benchmark.score;
    const label = BENCH_LEVELS[benchmark.score]?.label ?? '';
    const industry =
      auditData.entity?.description?.Industry?.name || 'Industry';
    auditData.industryBenchmarkDescription = label
      ? `${label} in ${industry}`
      : '';
  }

  public getEntityInformation(params: any): Observable<AuditData> {
    return new Observable((observer) => {
      this.sentimentService
        .getSentiment({ ...params, getSanctions: true }, false)
        .subscribe({
          next: (results: any) => {
            const dataEntity: AuditData = { ...results };

            if (
              results.entity.type === 'portfolio' &&
              this.portfolioApi.activePortfolio
            ) {
              dataEntity.entity = {
                ...results.entity,
                name: this.portfolioApi.activePortfolio.name || '',
              };
            }

            if (params.type === 'pne') {
              const [prefix, suffix] = dataEntity.entity.alpha_id.split('.');
              dataEntity.entity.name = `${suffix} (${prefix})`;
            } else if (
              this.portfolioApi.activePortfolio?.marketPortfolio &&
              ['industry', 'supersector', 'sector', 'subsector'].includes(
                params.type,
              )
            ) {
              dataEntity.entity.type = params.type;
              dataEntity.entity.name =
                this.portfolioApi.activePortfolio.marketPortfolioName;
            }

            observer.next(dataEntity);
            observer.complete();
          },
          error: (error) => {
            console.error('Error fetching entity information:', error);
            observer.error(error);
          },
        });
    });
  }

  private fetchScore(
    scoreType: ScoreRequestTypes,
    params: any,
  ): Observable<ScoreResponse> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: { collapse_on: ['participant'] },
        body: {
          score_type: scoreType,
          time: params.time || '7',
        },
      })
      .pipe(
        map((response: ScoreTimeSeriesResponse) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          const lastScore = ScoreUtilsService.getLastScore(timeSeries);
          const { trendType, trendValue } =
            ScoreUtilsService.getBullBearTrend(lastScore);

          let sentimentDelta = 0;
          if (lastScore != null && timeSeries.length >= 2) {
            for (let i = timeSeries.length - 1; i >= 1; i--) {
              if (timeSeries[i].score != null) {
                const prevScore = timeSeries[i - 1]?.score;
                if (prevScore != null) {
                  sentimentDelta = timeSeries[i].score - prevScore;
                }
                break;
              }
            }
          }

          return {
            score: lastScore,
            trend_value: trendValue,
            trend_type: trendType,
            sentiment_delta: sentimentDelta,
            score_ts: timeSeries.map((item) => ({ score: item.score })),
          };
        }),
        catchError(() => of({ score: null, score_ts: [] })),
      );
  }
}
